import { Badge, Img, Switch } from "@chakra-ui/react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { IFilterSignature } from "./interface";
import {
  PaginatorInfo,
  Signature,
  useListSignaturesQuery,
} from "../../generated/graphql";
import { createColumnHelper } from "@tanstack/react-table";
import Filter from "./Filter";
import update from "immutability-helper";
import DefaultTable, {
  TableImperativeHandle,
  ExtendedDefaultTableProps,
} from "../DefaultTable";

const columnHelper = createColumnHelper<Signature>();

const TableSignature = forwardRef<
  TableImperativeHandle<Signature>,
  ExtendedDefaultTableProps<Signature, IFilterSignature> & {
    onChangeDefault?: (row: Signature) => void;
    activeOnly?: boolean;
  }
>(
  (
    {
      filterParams,
      onFilterSubmit,
      page,
      onPageChange,
      onChangeDefault,
      activeOnly,
      ...props
    },
    ref
  ) => {
    useImperativeHandle(ref, () => ({
      updateRow(data) {
        setDatas((datas) => {
          const index = datas.findIndex((elm) => elm.id === data.id);
          return update(datas, {
            [index]: {
              $set: { ...datas[index], ...data },
            },
          });
        });
      },
      refresh() {
        refetch();
      },
    }));

    const [localFilterParams, setLocalFilterParams] =
      useState<IFilterSignature>();
    const [localPage, setLocalPage] = useState<number>(1);
    const [datas, setDatas] = useState<Signature[]>([]);

    const columns = useMemo(() => {
      return [
        columnHelper.accessor("imageUrl", {
          header: () => "Signature Image",
          cell: (info) => (
            <Img
              w="auto"
              h="80px"
              border="1px solid #dedede"
              p={2}
              borderRadius="sm"
              src={info.getValue()}
            />
          ),
        }),
        columnHelper.accessor("name", {
          header: () => "Signature Name",
          cell: (info) => info.getValue(),
        }),
        columnHelper.accessor("isDefault", {
          id: "isDefault",
          header: () => "Default",
          cell: (info) => (
            <Switch
              isChecked={info.getValue()}
              onChange={() =>
                onChangeDefault && onChangeDefault(info.row.original)
              }
              readOnly={info.getValue()}
            />
          ),
        }),
        columnHelper.accessor("enable", {
          header: () => "Enabled",
          cell: (info) => (
            <Badge colorScheme={info.getValue() ? "green" : "red"}>
              {info.getValue() ? "Enabled" : "Disabled"}
            </Badge>
          ),
        }),
      ];
    }, []);

    useEffect(() => {
      if (filterParams !== undefined) setLocalFilterParams(filterParams);
    }, [filterParams]);

    useEffect(() => {
      if (page !== undefined) setLocalPage(page);
    }, [page]);

    useEffect(() => {
      setLocalFilterParams({ ...localFilterParams });
    }, []);

    const [paginator, setPaginator] = useState<PaginatorInfo>();

    const { data, loading, refetch } = useListSignaturesQuery({
      notifyOnNetworkStatusChange: true,
      variables: {
        key: localFilterParams?.key || undefined,
        enable: activeOnly ? true : undefined,
      },
    });

    const [isLazyLoading, setIsLazyLoading] = useState(true);

    useEffect(() => {
      setPaginator(data?.listSignatures.paginatorInfo);
      setDatas((data?.listSignatures.data || []) as Signature[]);
    }, [data]);

    useEffect(() => {
      setIsLazyLoading(loading);
    }, [loading, setIsLazyLoading]);

    useEffect(() => {
      refetch({
        page: localPage,
        key: localFilterParams?.key || undefined,
      });
    }, [localFilterParams, localPage, refetch]);

    return (
      <DefaultTable
        data={datas}
        columns={columns}
        isLoading={loading || isLazyLoading}
        getRowId={(row) => row.id}
        filterComponent={
          <Filter
            value={localFilterParams}
            onSubmit={(data) => {
              setLocalPage(1);
              setLocalFilterParams(data);
              onFilterSubmit && onFilterSubmit(data);
            }}
          />
        }
        paginator={paginator}
        onPageChange={(page) => {
          setLocalPage(page);
          onPageChange && onPageChange(page);
        }}
        {...props}
      />
    );
  }
);

export default TableSignature;
